import { __rest, __assign } from 'tslib';
import React__default, { Suspense } from 'react';
import { activeBusinessFiltersState, useTotalCount, useReference, dataViewPage, dataViewSize, dataViewDisplayMode, ComponentAugmentKey, SpecificDataviewType } from '@keepeek/commons';
import { Box, useTheme } from '@mui/material';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { withSuspense } from '../withSuspense/index.js';
import EmptyState from './EmptyState/index.js';
import SpecificDataview from './Modes/index.js';
import { ListDataViewLoading } from './Modes/ListDataView/ListDataViewLoading.js';
import { MosaicDataViewLoading } from './Modes/MosaicDataView/MosaicDataViewLoading.js';
import NotFoundReference from './NotFoundReference/index.js';
import Paginate from './Paginate/index.js';
import TooManyState from './TooManyState/index.js';

var DataviewWithSuspense = function (_a) {
    var className = _a.className, KGrid = _a.KGrid, dataViewKey = _a.dataViewKey, _b = _a.hideFolderFiterInActiveFilters, hideFolderFiterInActiveFilters = _b === void 0 ? true : _b, props = __rest(_a, ["className", "KGrid", "dataViewKey", "hideFolderFiterInActiveFilters"]);
    return (React__default.createElement(Box, { id: "DataView", className: clsx("Dataview", className), sx: dataviewSX },
        React__default.createElement(Suspense, { fallback: React__default.createElement(DataviewLoading, { KGrid: KGrid, dataViewKey: dataViewKey }) },
            React__default.createElement(Dataview, __assign({ KGrid: KGrid, dataViewKey: dataViewKey, hideFolderFiterInActiveFilters: hideFolderFiterInActiveFilters }, props)))));
};
var Dataview = function (_a) {
    var _b;
    var KGrid = _a.KGrid, KIcon = _a.KIcon, goToPageWithBackContext = _a.goToPageWithBackContext, sentryCallback = _a.sentryCallback, shareTokenDownloadAuthorized = _a.shareTokenDownloadAuthorized, maxElementsPerPage = _a.maxElementsPerPage, isFolderSearch = _a.isFolderSearch, resetFilters = _a.resetFilters, GetCustomerAugmentedComponent = _a.GetCustomerAugmentedComponent, enabledActions = _a.enabledActions, allowSelection = _a.allowSelection, allowNavigation = _a.allowNavigation, onDataViewFirstRender = _a.onDataViewFirstRender, dataViewKey = _a.dataViewKey, onElementActionClick = _a.onElementActionClick, onElementDoubleClick = _a.onElementDoubleClick, _c = _a.hideFolderFiterInActiveFilters, hideFolderFiterInActiveFilters = _c === void 0 ? true : _c, actionButtonsColor = _a.actionButtonsColor, attachmentsVisible = _a.attachmentsVisible;
    var filters = useRecoilValue(activeBusinessFiltersState(dataViewKey));
    var totalCount = useTotalCount({ key: dataViewKey, filters: filters || null });
    var references = useReference({ key: dataViewKey, filters: filters });
    var isEmpty = totalCount === 0;
    var page = useRecoilValue(dataViewPage(dataViewKey));
    var size = useRecoilValue(dataViewSize(dataViewKey));
    var mode = useRecoilValue(dataViewDisplayMode(dataViewKey));
    var theme = useTheme();
    if (size * page > 10000 && totalCount === 0) {
        return React__default.createElement(TooManyState, { dataViewKey: dataViewKey });
    }
    if (isEmpty) {
        return (React__default.createElement(EmptyState, { isFolderSearch: isFolderSearch, filtersCount: (filters === null || filters === void 0 ? void 0 : filters.length) || 0, resetFilters: resetFilters, dataViewKey: dataViewKey, hideFolderFiterInActiveFilters: hideFolderFiterInActiveFilters }));
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(GetCustomerAugmentedComponent, { overrideKey: ComponentAugmentKey.Paginate, Node: Paginate, props: {
                size: "small",
                id: "paginationTop",
                sx: { paddingTop: 1, paddingRight: 2, paddingBottom: 0, paddingLeft: 2 },
                maxElementsPerPage: maxElementsPerPage,
                allowSelection: allowSelection,
                dataViewKey: dataViewKey
            } }),
        React__default.createElement(Box, { "data-testid": "Dataview", sx: (_b = {
                    paddingTop: 1,
                    paddingRight: 2,
                    paddingBottom: 2,
                    paddingLeft: 2
                },
                _b[theme.breakpoints.down("md")] = {
                    paddingRight: 1,
                    paddingLeft: 1
                },
                _b) },
            React__default.createElement(Box, null,
                React__default.createElement(NotFoundReference, { references: references }),
                React__default.createElement(SpecificDataview, { sentryCallback: sentryCallback, type: mode, KGrid: KGrid, KIcon: KIcon, goToPageWithBackContext: goToPageWithBackContext, shareTokenDownloadAuthorized: shareTokenDownloadAuthorized, enabledActions: enabledActions, allowSelection: allowSelection, allowNavigation: allowNavigation, onDataViewFirstRender: onDataViewFirstRender, dataViewKey: dataViewKey, onElementDoubleClick: onElementDoubleClick, onElementActionClick: onElementActionClick, actionButtonsColor: actionButtonsColor, attachmentsVisible: attachmentsVisible }))),
        React__default.createElement(GetCustomerAugmentedComponent, { overrideKey: ComponentAugmentKey.Paginate, Node: Paginate, props: {
                showSize: true,
                size: "small",
                id: "paginationBottom",
                sx: { paddingTop: 0, paddingRight: 2, paddingBottom: 10, paddingLeft: 2 },
                maxElementsPerPage: maxElementsPerPage,
                dataViewKey: dataViewKey
            } })));
};
var DataviewLoading = function (_a) {
    var _b;
    var KGrid = _a.KGrid, dataViewKey = _a.dataViewKey;
    var specificDataviewLoading;
    var mode = useRecoilValue(dataViewDisplayMode(dataViewKey));
    var theme = useTheme();
    switch (mode) {
        case SpecificDataviewType.LIST:
            specificDataviewLoading = React__default.createElement(ListDataViewLoading, { dataViewKey: dataViewKey });
            break;
        default:
            specificDataviewLoading = React__default.createElement(MosaicDataViewLoading, { dataViewKey: dataViewKey, KGrid: KGrid });
    }
    return (React__default.createElement(Box, { id: "DataView", className: clsx("Dataview"), sx: dataviewSX },
        React__default.createElement(Box, { sx: (_b = {
                    paddingTop: 1,
                    paddingRight: 2,
                    paddingBottom: 2,
                    paddingLeft: 2
                },
                _b[theme.breakpoints.down("md")] = {
                    paddingRight: 1,
                    paddingLeft: 1
                },
                _b) }, specificDataviewLoading)));
};
var dataviewSX = {
    backgroundColor: function (theme) { return theme.palette.common.white; },
    position: "relative",
    minHeight: "100%"
};
// Wrap with suspense for errors
var Dataview$1 = withSuspense(DataviewWithSuspense);

export { DataviewLoading, Dataview$1 as default };
//# sourceMappingURL=index.js.map
